"use client";

import React, {
    useState,
} from "react";

import Link from "next/link";

import {
    ArrowLeft,
    Star,
    Send,
    MessageSquare,
    Sparkles,
    CheckCircle2,
    Quote,
    Building2,
} from "lucide-react";

import { useRouter } from "next/navigation";

import { apiurl } from "@/app/common/apiurl";

import {
    ToastError,
    ToastSuccess,
} from "@/app/common/ToastNotifications";


export default function page() {

    let router = useRouter();


    // =========================================================
    // STATES
    // =========================================================

    let [inputFields, setInputFields] =
        useState({
            Company_Name: "",
            Rating: 0,
            Client_Review: "",
        });


    let [hoverRating, setHoverRating] =
        useState(0);


    let [submitted, setSubmitted] =
        useState(false);


    let [loading, setLoading] =
        useState(false);


    // =========================================================
    // RATING TEXT
    // =========================================================

    let ratingText = {

        1: {
            title:
                "Not great",

            description:
                "We are sorry your experience did not meet expectations.",
        },

        2: {
            title:
                "Could be better",

            description:
                "Your feedback helps us understand what we can improve.",
        },

        3: {
            title:
                "Good experience",

            description:
                "Thanks for sharing your experience with us.",
        },

        4: {
            title:
                "Great experience",

            description:
                "We are glad you had a good experience with us.",
        },

        5: {
            title:
                "Excellent experience",

            description:
                "Thank you for your wonderful feedback!",
        },

    };


    // =========================================================
    // ACTIVE RATING
    // =========================================================

    let activeRating =
        hoverRating ||
        inputFields.Rating;


    // =========================================================
    // SUBMIT TESTIMONIAL
    // =========================================================

    let handleSubmit = async (
        e
    ) => {

        e.preventDefault();


        // =====================================================
        // RATING VALIDATION
        // =====================================================

        if (
            inputFields.Rating === 0
        ) {

            ToastError(
                "Please select a rating."
            );

            return;

        }


        // =====================================================
        // COMPANY VALIDATION
        // =====================================================

        if (
            !inputFields.Company_Name.trim()
        ) {

            ToastError(
                "Please enter your company name."
            );

            return;

        }


        // =====================================================
        // REVIEW VALIDATION
        // =====================================================

        if (
            !inputFields.Client_Review.trim()
        ) {

            ToastError(
                "Please write your review."
            );

            return;

        }


        try {

            setLoading(
                true
            );


            // =================================================
            // ADD TESTIMONIAL API
            // =================================================

            let response =
                await apiurl.post(
                    "/web/add-testimonials",
                    inputFields,
                    {
                        headers: {

                            Authorization:
                                JSON.parse(
                                    localStorage.getItem(
                                        "usertoken"
                                    )
                                ),

                            csrftoken:
                                JSON.parse(
                                    sessionStorage.getItem(
                                        "csrfToken"
                                    )
                                ),

                        },
                    }
                );

            if (
                response?.data?.Redirect
            ) {

                router.push(
                    response.data.Redirect
                );

                return;

            }


            // =================================================
            // SUCCESS
            // =================================================

            if (
                response?.data?.Status ===
                1
            ) {

                setSubmitted(
                    true
                );


                ToastSuccess(
                    response?.data?.Message
                );

            }

            else {

                ToastError(
                    response?.data?.Message
                );

            }

        }

        catch (
        error
        ) {

            console.log(
                "Add Testimonial Error:",
                error
            );


            if (
                error?.response?.data?.Redirect
            ) {

                router.push(
                    error.response.data.Redirect
                );

                return;

            }


            ToastError(
                error?.response?.data?.Message ||
                "Something went wrong while submitting your testimonial."
            );

        }

        finally {

            setLoading(
                false
            );

        }

    };


    // =========================================================
    // WRITE ANOTHER REVIEW
    // =========================================================

    let writeAnotherReview = () => {

        setInputFields({
            Company_Name:
                "",

            Rating:
                0,

            Client_Review:
                "",
        });


        setHoverRating(
            0
        );


        setSubmitted(
            false
        );

    };


    // =========================================================
    // RETURN
    // =========================================================

    return (

        <main className="min-h-screen bg-[#F7F7F7]">

            <div className="mx-auto max-w-6xl px-4 py-8 md:px-8 md:py-12 lg:px-10">


                {/* =================================================
                    TOP NAV
                ================================================= */}

                <div className="mb-8">

                    <Link
                        href="/pages/web/profile"
                        className="group inline-flex items-center gap-2 text-sm font-medium text-gray-500 transition hover:text-[#0A2342]"
                    >

                        <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-white transition group-hover:border-[#0A2342]/20 group-hover:bg-[#0A2342] group-hover:text-white">

                            <ArrowLeft
                                size={16}
                            />

                        </span>


                        Back To Profile

                    </Link>

                </div>


                {/* =================================================
                    HERO
                ================================================= */}

                <section className="relative overflow-hidden rounded-[28px] bg-[#0A2342] px-6 py-8 shadow-xl md:px-10 md:py-10">

                    <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full border border-white/10" />


                    <div className="pointer-events-none absolute -bottom-32 right-16 h-80 w-80 rounded-full border border-[#D4AF37]/10" />


                    <div className="pointer-events-none absolute left-1/2 top-1/2 h-40 w-40 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#D4AF37]/5 blur-3xl" />


                    <div className="relative max-w-3xl">

                        <div className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/20 bg-[#D4AF37]/10 px-3 py-1.5">

                            <Sparkles
                                size={14}
                                className="text-[#D4AF37]"
                            />


                            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">
                                Your Voice Matters
                            </span>

                        </div>


                        <h1 className="mt-5 text-3xl font-semibold leading-tight text-white sm:text-4xl md:text-5xl">
                            Tell us how we are doing.
                        </h1>


                        <p className="mt-4 max-w-2xl text-sm leading-7 text-white/60 md:text-base">
                            Your experience means a lot to us.
                            Share your thoughts about Vanshi
                            Textile and help us create an even
                            better experience.
                        </p>

                    </div>

                </section>


                {/* =================================================
                    CONTENT
                ================================================= */}

                <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">


                    {/* =================================================
                        REVIEW CARD
                    ================================================= */}

                    <section className="overflow-hidden rounded-[26px] border border-gray-100 bg-white shadow-sm">


                        {
                            submitted ? (

                                /* =================================================
                                    SUCCESS STATE
                                ================================================= */

                                <div className="flex min-h-130 flex-col items-center justify-center px-6 py-16 text-center md:px-10">


                                    {/* SUCCESS ICON */}

                                    <div className="relative">

                                        <div className="absolute inset-0 scale-150 rounded-full bg-green-100 blur-2xl" />


                                        <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-green-50">

                                            <CheckCircle2
                                                size={40}
                                                className="text-green-600"
                                            />

                                        </div>

                                    </div>


                                    {/* TITLE */}

                                    <p className="mt-7 text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">
                                        Testimonial Received
                                    </p>


                                    <h2 className="mt-2 text-3xl font-semibold text-[#0A2342]">
                                        Thank You!
                                    </h2>


                                    <p className="mt-3 max-w-md text-sm leading-6 text-gray-500">

                                        Your feedback has been successfully
                                        received. We genuinely appreciate
                                        you taking the time to share your
                                        experience with Vanshi Textile.

                                    </p>


                                    {/* COMPANY NAME */}

                                    <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-4 py-2">

                                        <Building2
                                            size={15}
                                            className="text-[#9c7d16]"
                                        />


                                        <span className="text-xs font-semibold text-[#0A2342]">

                                            {
                                                inputFields.Company_Name
                                            }

                                        </span>

                                    </div>


                                    {/* RATING */}

                                    <div className="mt-5 flex items-center gap-1">

                                        {
                                            [
                                                1,
                                                2,
                                                3,
                                                4,
                                                5,
                                            ].map(
                                                (
                                                    value
                                                ) => (

                                                    <Star
                                                        key={
                                                            value
                                                        }
                                                        size={20}
                                                        className={
                                                            value <=
                                                                inputFields.Rating

                                                                ? "fill-[#D4AF37] text-[#D4AF37]"

                                                                : "text-gray-200"
                                                        }
                                                    />

                                                )
                                            )
                                        }

                                    </div>


                                    {/* ACTIONS */}

                                    <div className="mt-8 flex flex-col gap-3 sm:flex-row">

                                        <Link
                                            href="/pages/web/profile"
                                            className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-6 py-3 text-sm font-semibold text-gray-600 transition hover:bg-gray-50"
                                        >
                                            Back To Profile
                                        </Link>

                                    </div>

                                </div>

                            ) : (

                                /* =================================================
                                    REVIEW FORM
                                ================================================= */

                                <form
                                    onSubmit={
                                        handleSubmit
                                    }
                                    className="p-6 md:p-10"
                                >


                                    {/* =================================================
                                        FORM HEADING
                                    ================================================= */}

                                    <div>

                                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">
                                            Customer Testimonial
                                        </p>


                                        <h2 className="mt-2 text-2xl font-semibold text-[#0A2342]">
                                            How was your experience?
                                        </h2>


                                        <p className="mt-2 max-w-xl text-sm leading-6 text-gray-500">
                                            Share your company details,
                                            rating and experience with us.
                                        </p>

                                    </div>


                                    {/* =================================================
                                        COMPANY NAME
                                    ================================================= */}

                                    <div className="mt-8">


                                        <div className="flex items-center gap-3">

                                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0A2342]/5">

                                                <Building2
                                                    size={18}
                                                    className="text-[#0A2342]"
                                                />

                                            </div>


                                            <div>

                                                <label
                                                    htmlFor="companyName"
                                                    className="text-sm font-semibold text-[#0A2342]"
                                                >
                                                    Company Name
                                                </label>


                                                <p className="text-xs text-gray-400">
                                                    Enter your company or business name
                                                </p>

                                            </div>

                                        </div>


                                        <div className="mt-4">

                                            <input
                                                id="companyName"
                                                type="text"
                                                name="Company_Name"
                                                value={
                                                    inputFields.Company_Name
                                                }
                                                onChange={(e) => {

                                                    setInputFields({
                                                        ...inputFields,
                                                        Company_Name:
                                                            e.target.value,
                                                    });

                                                }}
                                                maxLength={
                                                    100
                                                }
                                                placeholder="Enter your company name"
                                                className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3.5 text-sm leading-6 text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-[#D4AF37] focus:ring-4 focus:ring-[#D4AF37]/10"
                                            />

                                        </div>

                                    </div>


                                    {/* =================================================
                                        STAR RATING
                                    ================================================= */}

                                    <div className="mt-8 rounded-2xl border border-gray-100 bg-[#FAFAFA] p-5 md:p-6">


                                        <div className="flex flex-col items-center text-center">


                                            <p className="text-sm font-semibold text-[#0A2342]">
                                                Your Rating
                                            </p>


                                            <p className="mt-1 text-xs text-gray-400">
                                                Tap a star to rate your experience
                                            </p>


                                            {/* STARS */}

                                            <div className="mt-6 flex items-center gap-1.5 sm:gap-3">

                                                {
                                                    [
                                                        1,
                                                        2,
                                                        3,
                                                        4,
                                                        5,
                                                    ].map(
                                                        (
                                                            value
                                                        ) => {

                                                            let isActive =
                                                                value <=
                                                                activeRating;


                                                            return (

                                                                <button
                                                                    key={
                                                                        value
                                                                    }
                                                                    type="button"
                                                                    onClick={() => {

                                                                        setInputFields({
                                                                            ...inputFields,
                                                                            Rating:
                                                                                value,
                                                                        });

                                                                    }}
                                                                    onMouseEnter={() =>
                                                                        setHoverRating(
                                                                            value
                                                                        )
                                                                    }
                                                                    onMouseLeave={() =>
                                                                        setHoverRating(
                                                                            0
                                                                        )
                                                                    }
                                                                    className="group rounded-xl p-1 transition focus:outline-none"
                                                                    aria-label={
                                                                        `Give ${value} star rating`
                                                                    }
                                                                >

                                                                    <Star
                                                                        size={40}
                                                                        strokeWidth={1.7}
                                                                        className={

                                                                            isActive

                                                                                ? "fill-[#D4AF37] text-[#D4AF37] transition"

                                                                                : "text-gray-300 transition group-hover:text-[#D4AF37]"

                                                                        }
                                                                    />

                                                                </button>

                                                            );

                                                        }
                                                    )
                                                }

                                            </div>


                                            {/* RATING DESCRIPTION */}

                                            <div className="mt-5 min-h-15.5">

                                                {
                                                    activeRating > 0 ? (

                                                        <>

                                                            <p className="text-base font-semibold text-[#0A2342]">

                                                                {
                                                                    ratingText[
                                                                        activeRating
                                                                    ]?.title
                                                                }

                                                            </p>


                                                            <p className="mt-1 max-w-md text-xs leading-5 text-gray-500">

                                                                {
                                                                    ratingText[
                                                                        activeRating
                                                                    ]?.description
                                                                }

                                                            </p>

                                                        </>

                                                    ) : (

                                                        <p className="pt-2 text-sm text-gray-400">
                                                            Your honest rating helps us improve.
                                                        </p>

                                                    )

                                                }

                                            </div>

                                        </div>

                                    </div>


                                    {/* =================================================
                                        CLIENT REVIEW
                                    ================================================= */}

                                    <div className="mt-8">


                                        <div className="flex items-start justify-between gap-4">


                                            <div className="flex items-center gap-3">


                                                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0A2342]/5">

                                                    <MessageSquare
                                                        size={18}
                                                        className="text-[#0A2342]"
                                                    />

                                                </div>


                                                <div>

                                                    <label
                                                        htmlFor="clientReview"
                                                        className="text-sm font-semibold text-[#0A2342]"
                                                    >
                                                        Tell us more
                                                    </label>


                                                    <p className="text-xs text-gray-400">
                                                        Share your thoughts with us
                                                    </p>

                                                </div>

                                            </div>


                                            <span className="text-xs text-gray-400">

                                                {
                                                    inputFields.Client_Review.length
                                                }

                                                /200

                                            </span>

                                        </div>


                                        <div className="relative mt-4">

                                            <textarea
                                                id="clientReview"
                                                name="Client_Review"
                                                value={
                                                    inputFields.Client_Review
                                                }
                                                onChange={(e) => {

                                                    setInputFields({
                                                        ...inputFields,
                                                        Client_Review:
                                                            e.target.value,
                                                    });

                                                }}
                                                maxLength={
                                                    200
                                                }
                                                rows={
                                                    7
                                                }
                                                placeholder="What did you like? What can we improve?"
                                                className="w-full resize-none rounded-2xl border border-gray-200 bg-white px-4 py-4 text-sm leading-6 text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-[#D4AF37] focus:ring-4 focus:ring-[#D4AF37]/10"
                                            />

                                        </div>

                                    </div>


                                    {/* =================================================
                                        ACTIONS
                                    ================================================= */}

                                    <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">


                                        <Link
                                            href="/pages/web/profile"
                                            className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-6 py-3.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-50"
                                        >
                                            Cancel
                                        </Link>


                                        <button
                                            type="submit"
                                            disabled={
                                                loading
                                            }
                                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#0A2342] px-7 py-3.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#12365f] disabled:cursor-not-allowed disabled:opacity-60"
                                        >

                                            {
                                                loading
                                                    ? (

                                                        <>

                                                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />

                                                            Submitting...

                                                        </>

                                                    )
                                                    : (

                                                        <>

                                                            <Send
                                                                size={17}
                                                            />

                                                            Submit Testimonial

                                                        </>

                                                    )
                                            }

                                        </button>

                                    </div>


                                </form>

                            )
                        }

                    </section>


                    {/* =================================================
                        SIDEBAR
                    ================================================= */}

                    <aside className="space-y-6">


                        {/* =================================================
                            TRUST CARD
                        ================================================= */}

                        <div className="rounded-[26px] bg-[#0A2342] p-6 text-white shadow-lg">


                            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#D4AF37]">

                                <Quote
                                    size={20}
                                    className="text-[#0A2342]"
                                />

                            </div>


                            <h3 className="mt-5 text-lg font-semibold">
                                Every testimonial matters.
                            </h3>


                            <p className="mt-3 text-sm leading-6 text-white/55">
                                Honest customer feedback helps us
                                improve our products, services and
                                overall experience.
                            </p>


                            <div className="mt-6 h-px bg-white/10" />


                            <div className="mt-5 flex items-center gap-2">


                                <div className="flex -space-x-1">

                                    {
                                        [
                                            1,
                                            2,
                                            3,
                                            4,
                                            5,
                                        ].map(
                                            (
                                                value
                                            ) => (

                                                <div
                                                    key={
                                                        value
                                                    }
                                                    className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-[#0A2342] bg-white"
                                                >

                                                    <Star
                                                        size={12}
                                                        className="fill-[#D4AF37] text-[#D4AF37]"
                                                    />

                                                </div>

                                            )
                                        )
                                    }

                                </div>


                                <span className="text-xs text-white/50">
                                    Your feedback matters
                                </span>

                            </div>

                        </div>


                        {/* =================================================
                            REVIEW TIPS
                        ================================================= */}

                        <div className="rounded-[26px] border border-gray-100 bg-white p-6 shadow-sm">


                            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#D4AF37]">
                                Helpful Tips
                            </p>


                            <h3 className="mt-2 text-lg font-semibold text-[#0A2342]">
                                What can you share?
                            </h3>


                            <div className="mt-5 space-y-4">


                                {/* TIP 1 */}

                                <div className="flex gap-3">

                                    <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-50 text-green-600">

                                        <CheckCircle2
                                            size={15}
                                        />

                                    </div>


                                    <p className="text-sm leading-5 text-gray-500">
                                        Your overall shopping experience.
                                    </p>

                                </div>


                                {/* TIP 2 */}

                                <div className="flex gap-3">

                                    <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-50 text-green-600">

                                        <CheckCircle2
                                            size={15}
                                        />

                                    </div>


                                    <p className="text-sm leading-5 text-gray-500">
                                        Product quality and service.
                                    </p>

                                </div>


                                {/* TIP 3 */}

                                <div className="flex gap-3">

                                    <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-50 text-green-600">

                                        <CheckCircle2
                                            size={15}
                                        />

                                    </div>


                                    <p className="text-sm leading-5 text-gray-500">
                                        Anything we can do better.
                                    </p>

                                </div>


                                {/* TIP 4 */}

                                <div className="flex gap-3">

                                    <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-50 text-green-600">

                                        <CheckCircle2
                                            size={15}
                                        />

                                    </div>


                                    <p className="text-sm leading-5 text-gray-500">
                                        Your company or business name.
                                    </p>

                                </div>


                            </div>

                        </div>


                    </aside>

                </div>


                {/* =================================================
                    BOTTOM NOTE
                ================================================= */}

                <div className="mt-6 text-center">

                    <p className="text-xs text-gray-400">
                        Thank you for being a valued Vanshi Textile customer.
                    </p>

                </div>


            </div>

        </main>

    );

}